// Session management types and contracts

export const SESSION_STATUS = {
  INITIALIZING: 'initializing',
  GATHERING: 'gathering', 
  REVIEWING: 'reviewing',
  GENERATING: 'generating',
  COMPLETE: 'complete',
  ERROR: 'error'
};

export const QUESTION_CATEGORIES = {
  BUSINESS: 'business',
  SECURITY: 'security',
  RELIABILITY: 'reliability', 
  PERFORMANCE: 'performance',
  INTEGRATIONS: 'integrations',
  DATA: 'data',
  OPERATIONS: 'operations',
  VALIDATION: 'validation'
};

export const ARTIFACT_TYPES = {
  // Phase 1 - Always generated after business + basic NFRs
  C4_CONTEXT: 'c4_context',
  C4_CONTAINER: 'c4_container', 
  HLD_OUTLINE: 'hld_outline',
  SAD_OUTLINE: 'sad_outline',
  
  // Phase 2 - Generated after data entities captured
  ERD: 'erd',
  
  // Phase 3 - Generated after user journeys + endpoints defined
  SEQUENCE_DIAGRAM: 'sequence_diagram',
  OPENAPI_SPEC: 'openapi_spec',
  
  // Phase 4 - Generated after hosting choices made
  DEPLOYMENT_DIAGRAM: 'deployment_diagram',
  INFRASTRUCTURE_CODE: 'infrastructure_code'
};

export const ARTIFACT_PHASES = {
  FOUNDATION: 'foundation', // C4, HLD/SAD outlines
  DATA_MODEL: 'data_model', // ERD
  API_DESIGN: 'api_design', // Sequence, OpenAPI
  DEPLOYMENT: 'deployment'  // Infrastructure diagrams/code
};

// Session data structure
export const createSession = (userId, projectName) => ({
  sessionId: crypto.randomUUID(),
  userId,
  projectName: projectName || 'Untitled Project',
  status: SESSION_STATUS.INITIALIZING,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  initialIdea: '',
  currentQuestionIndex: 0,
  questions: [],
  answers: {},
  uploads: [],
  requirements: null,
  artifacts: [],
  metadata: {
    totalQuestions: 0,
    completedQuestions: 0,
    estimatedTimeRemaining: 0,
    currentCategory: null,
    completedCategories: [],
    unlockedArtifactPhases: ['foundation'] // Always start with foundation
  }
});

// Question structure
export const createQuestion = (id, category, text, options = null, required = true, weight = 1) => ({
  id,
  category,
  text,
  options, // For multiple choice questions
  required,
  weight, // Importance for artifact generation decisions
  answered: false,
  answer: null,
  followUpQuestions: [],
  unlocks: [] // Which artifact phases this question helps unlock
});

// Answer structure  
export const createAnswer = (questionId, value, metadata = {}) => ({
  questionId,
  value,
  timestamp: new Date().toISOString(),
  metadata // For additional context like confidence, source, etc.
});

// Upload structure
export const createUpload = (file, type = 'document') => ({
  id: crypto.randomUUID(),
  filename: file.name,
  size: file.size,
  type,
  mimeType: file.type,
  uploadedAt: new Date().toISOString(),
  processed: false,
  extractedContent: null
});

// Artifact structure
export const createArtifact = (type, title, description) => ({
  id: crypto.randomUUID(),
  type,
  title,
  description,
  status: 'pending',
  createdAt: new Date().toISOString(),
  downloadUrl: null,
  previewUrl: null,
  metadata: {}
});

// Requirements document structure
export const createRequirements = (session) => ({
  id: crypto.randomUUID(),
  sessionId: session.sessionId,
  projectName: session.projectName,
  initialIdea: session.initialIdea,
  
  // Progressive requirement categories
  business: {
    problemStatement: '',
    productTypes: [],
    volumeMetrics: {},
    targetRegions: [],
    slas: {},
    compliance: []
  },
  security: {
    identityProvider: '',
    tokenManagement: {},
    keyManagement: '',
    dataClassification: []
  },
  reliability: {
    rto: null,
    rpo: null,
    availabilityZones: '',
    retryStrategy: {},
    backupStrategy: {}
  },
  performance: {
    readWriteMix: '',
    cachingNeeds: [],
    searchRequirements: '',
    recommendationEngine: false
  },
  integrations: {
    paymentGateways: [],
    externalSystems: [],
    notifications: []
  },
  data: {
    coreEntities: [],
    relationships: [],
    retention: {},
    residency: ''
  },
  operations: {
    deploymentStrategy: '',
    observabilityStack: [],
    budgetPosture: ''
  },
  
  // Derived insights
  unlockedPhases: [],
  confidence: {},
  gaps: [],
  generatedAt: new Date().toISOString()
});